import { ChangeEvent, DragEvent, useCallback, useState } from 'react';
import {
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Users,
  Building2,
} from 'lucide-react';

import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';

import { useInvestorImportUpload } from '@/hooks/react-query/useInvestorImport';
import type { ImportValidationResult } from '../../types/investor-import';

interface FileUploadStepProps {
  onFileUploaded: (result: ImportValidationResult, sessionId: string) => void;
}

export function FileUploadStep({ onFileUploaded }: FileUploadStepProps) {
  const [dragActive, setDragActive] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ImportValidationResult | null>(null);
  const uploadMutation = useInvestorImportUpload();

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return;

      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        // The mutation will handle the error toast
        return;
      }

      try {
        const result = await uploadMutation.mutateAsync(file);
        setValidationResult(result);

        if (result.is_valid && result.session_id) {
          onFileUploaded(result, result.session_id);
        }
      } catch (error) {
        // Error is already handled by the mutation's onError
        console.error('Upload failed:', error);
      }
    },
    [uploadMutation, onFileUploaded]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(e.dataTransfer.files[0]);
      }
    },
    [handleFileUpload]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileUpload(e.target.files[0]);
      }
    },
    [handleFileUpload]
  );

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Upload DFF Compliance File
          </CardTitle>
          <CardDescription>
            Upload your DFF Compliance Excel file (16.0 format) to begin the
            import process.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${uploadMutation.isPending ? 'pointer-events-none opacity-50' : ''}
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="flex flex-col items-center space-y-4">
              <Upload
                className={`w-12 h-12 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`}
              />

              <div>
                <p className="text-lg font-medium text-gray-900">
                  {uploadMutation.isPending
                    ? 'Processing file...'
                    : 'Drop your Excel file here'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Or click to browse and select a file
                </p>
              </div>

              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={uploadMutation.isPending}
                className="hidden"
                id="file-upload"
              />

              <Button
                variant="outline"
                disabled={uploadMutation.isPending}
                onClick={() => document.getElementById('file-upload')?.click()}
                type="button"
              >
                Browse Files
              </Button>

              <div className="text-xs text-gray-500">
                Supported formats: .xlsx, .xls (Max size: 10MB)
              </div>
            </div>
          </div>

          {/* Error Display */}
          {uploadMutation.error && (
            <Alert className="mt-4 border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {uploadMutation.error instanceof Error
                  ? uploadMutation.error.message
                  : 'Failed to upload file'}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Validation Results */}
      {validationResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {validationResult.is_valid ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              File Validation Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    Investors
                  </span>
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {validationResult.total_investors}
                </div>
              </div>

              <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900">
                    Contacts
                  </span>
                </div>
                <div className="text-2xl font-bold text-green-900">
                  {validationResult.total_contacts}
                </div>
              </div>

              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">
                    Funds
                  </span>
                </div>
                <div className="text-2xl font-bold text-purple-900">
                  {validationResult.unique_fund_names.length}
                </div>
              </div>

              <div
                className={`p-3 rounded-lg ${
                  validationResult.is_valid ? 'bg-green-50' : 'bg-red-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {validationResult.is_valid ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span
                    className={`text-sm font-medium ${
                      validationResult.is_valid
                        ? 'text-green-900'
                        : 'text-red-900'
                    }`}
                  >
                    Status
                  </span>
                </div>
                <div
                  className={`text-sm font-bold ${
                    validationResult.is_valid
                      ? 'text-green-900'
                      : 'text-red-900'
                  }`}
                >
                  {validationResult.is_valid ? 'Valid' : 'Invalid'}
                </div>
              </div>
            </div>

            {/* Fund Names Preview */}
            {validationResult.unique_fund_names.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Funds Found in File:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {validationResult.unique_fund_names.map((fundName) => (
                    <Badge key={fundName} variant="outline">
                      {fundName}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {validationResult.validation_errors.length > 0 && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription>
                  <div className="font-medium text-red-800 mb-2">
                    Validation Errors:
                  </div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {validationResult.validation_errors.map((error) => (
                      <li key={error}>• {error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Validation Warnings */}
            {validationResult.validation_warnings.length > 0 && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription>
                  <div className="font-medium text-yellow-800 mb-2">
                    Warnings:
                  </div>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {validationResult.validation_warnings.map((warning) => (
                      <li key={warning}>• {warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Sample Data Preview */}
            {validationResult.investors_preview.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Sample Investors (First 3):
                </h4>
                <div className="space-y-2">
                  {validationResult.investors_preview
                    .slice(0, 3)
                    .map((investor) => (
                      <div
                        key={investor.row_number}
                        className="border rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{investor.name}</div>
                            <div className="text-sm text-gray-600">
                              {investor.investor_type} • {investor.country}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {investor.has_banking_info && (
                              <Badge variant="outline" className="text-xs">
                                Banking
                              </Badge>
                            )}
                            {investor.has_compliance_info && (
                              <Badge variant="outline" className="text-xs">
                                Compliance
                              </Badge>
                            )}
                          </div>
                        </div>
                        {investor.fund_names.length > 0 && (
                          <div className="mt-2 text-xs text-gray-600">
                            Funds: {investor.fund_names.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                {validationResult.investors_preview.length > 3 && (
                  <p className="text-sm text-gray-500 mt-2">
                    ... and {validationResult.investors_preview.length - 3} more
                    investors
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Expected File Format Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Expected File Format</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600">
          <div className="space-y-2">
            <p>
              • DFF Compliance Excel file with &quot;INVESTOR ID ATOM&quot; and
              &quot;PEOPLE ID ATOM&quot; sheets
            </p>
            <p>
              • Investor data including legal names, types, commitments, and
              compliance information
            </p>
            <p>
              • Contact data with names, emails, roles, and associations to
              investors
            </p>
            <p>• Banking information (IBAN, SWIFT codes) and KYC/AML data</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
